import React, { useRef } from 'react';

import config from 'config';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

import { findKeyInNestedStructure } from '~/account/components/InitiateReturnLayout/helper';
import WishlistToggleButton from '~/shared/components/WishlistToggleButton';
import { isReactVideoEnded } from '~/shared/utils/helpers';
import useSecondarySite from '~/shared/utils/useSecondarySite';
import {
  trackVideoPlaybackStarted,
  trackVideoPlaybackCompleted,
} from '~/shoppableVideo/actions';
import {
  ImageBrowser,
  Picture,
  useProductDetailContext,
  useProductImages,
  useTheme,
  useModelImageContext,
  SkeletonLine,
  Video,
} from '~/techstyle-shared/react-components';
import { useSession } from '~/techstyle-shared/redux-core';

import EnhancedMemberModel from '../EnhancedMemberModel';
import { useMemberModelContext } from '../EnhancedMemberModelProvider';
import ModelIndicator from '../ModelIndicator';

const aspectRatio = config.get('public.brand.pdpImageAndVideoAspectRatio');

const ImageWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 28px;
  position: relative;
`;

const HeroImageWrapper = styled.div`
  position: relative;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
`;

const ImageHeroSkeleton = styled(SkeletonLine)`
  height: 543px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet.maxWidth}px) {
    height: 300px;
  }
`;

const MemberModelWrapper = styled.div`
  ${({ theme: { pdpStyles } }) => pdpStyles.enhancedMemberModel.button.wrapper};
`;

const IndicatorWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 100%;
`;

const PictureWithIndicatorWrapper = styled.div`
  position: relative;
  padding-bottom: 24px;
`;

const VideoWrapper = styled.div``;

const toggleButtonStyle = css`
  position: absolute;
  width: 48px;
  height: 48px;
  top: 16px;
  right: 16px;
  z-index: 1;
  padding: 12px 10px;
`;

const heartIconStyle = css`
  width: 28px;
  height: 24px;
  color: ${({ theme }) => theme.colors.heartColor};
`;

export const ProductDetailImagesGridLoading = () => {
  return (
    <ImageWrapper>
      <HeroImageWrapper>
        <ImageHeroSkeleton />
      </HeroImageWrapper>
      <HeroImageWrapper>
        <ImageHeroSkeleton />
      </HeroImageWrapper>
      <HeroImageWrapper>
        <ImageHeroSkeleton />
      </HeroImageWrapper>
      <HeroImageWrapper>
        <ImageHeroSkeleton />
      </HeroImageWrapper>
    </ImageWrapper>
  );
};

export default function ProductDetailImagesGrid({ product: productFromProps }) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const productDetailFromCtx = useProductDetailContext();
  const product = productFromProps || productDetailFromCtx.product;
  const { isLoggedIn } = useSession();
  const secondarySite = useSecondarySite();
  const isMysteryBox = product.isMysteryBox;

  const { activeModelImages, modelSets: memberModels } = useModelImageContext();
  const [{ model }] = memberModels ?? [{ model: null }];
  const imageSets = useProductImages({
    images: activeModelImages,
  });
  const heroImageBreakpoints = [
    { ...theme.breakpoints.tablet, pictureWidth: 320 },
    { minWidth: 1024, pictureWidth: 800 },
  ];

  const hasTrackedRef = useRef(false);

  const hasSamVideo = findKeyInNestedStructure(imageSets, 'isVideo');
  const consoleVideoUrl = product.video && product.video.url;
  const showVideoFromConsole = consoleVideoUrl && !hasSamVideo;

  const { hasModelInfo } = useMemberModelContext();

  if (!imageSets.length) {
    return null;
  }

  const handleProgress = ({ playedSeconds, loadedSeconds }) => {
    const videoHasEnded = isReactVideoEnded(playedSeconds, loadedSeconds);
    if (videoHasEnded) {
      dispatch(trackVideoPlaybackCompleted());
    }
  };

  const handleOnPlay = () => {
    if (!hasTrackedRef.current) {
      dispatch(trackVideoPlaybackStarted());
      hasTrackedRef.current = true;
    }
  };

  const renderImage = (image, index) => {
    const shouldDisplayModelIndicator = model && hasModelInfo && index === 0;
    const imgUrl = image?.[0]?.images?.[0]?.url;
    // Edge case: imgUrl may potentially be not unique due to getting multiple images with the same URL from the API,
    // hence we append the index to ensure uniqueness, to avoid React key rendering issues.
    const pictureKey = imgUrl ? `${imgUrl}-${index}` : index;

    const pictureProps = {
      key: pictureKey,
      sources: image,
      alt: product.label,
      aspectRatio,
      breakpoints: heroImageBreakpoints,
      loading: 'lazy',
    };

    const isVideo = image?.[0]?.isVideo;
    const videoPath = image?.[0]?.videoPath;

    if (isVideo && videoPath) {
      return (
        <VideoWrapper key={videoPath}>
          <Video
            url={videoPath}
            aspectRatio={aspectRatio}
            data-autotag="pdp_video"
            onProgress={handleProgress}
            onPlay={handleOnPlay}
          />
        </VideoWrapper>
      );
    }

    if (shouldDisplayModelIndicator) {
      return (
        <PictureWithIndicatorWrapper
          key={pictureKey}
          data-testid="img-with-model-indicator"
        >
          <IndicatorWrapper>
            <ModelIndicator />
          </IndicatorWrapper>
          <Picture {...pictureProps} />
        </PictureWithIndicatorWrapper>
      );
    }

    return <Picture {...pictureProps} />;
  };

  return (
    <ImageBrowser images={imageSets}>
      <ImageWrapper data-testid="pdp-image-grid">
        {model && hasModelInfo && (
          <MemberModelWrapper>
            <EnhancedMemberModel product={product} />
          </MemberModelWrapper>
        )}
        {imageSets.map((image, index) => {
          // add the favorite icon to the second image at index 1, or first image if only 1 image exists
          const hasFloatingFavoriteButton =
            (imageSets.length === 1 && index === 0) ||
            (imageSets.length > 1 && index === 1);

          if (hasFloatingFavoriteButton) {
            return (
              <HeroImageWrapper key={index}>
                {isLoggedIn && !secondarySite && !isMysteryBox ? (
                  <WishlistToggleButton
                    autoTagPrefix="pdp"
                    toggleButtonStyle={toggleButtonStyle}
                    heartIconStyle={heartIconStyle}
                  />
                ) : null}
                {renderImage(image, index)}
              </HeroImageWrapper>
            );
          }
          return renderImage(image, index);
        })}
        {showVideoFromConsole && (
          <VideoWrapper>
            <Video
              url={consoleVideoUrl}
              aspectRatio={aspectRatio}
              data-autotag="pdp_video"
              onProgress={handleProgress}
              onPlay={handleOnPlay}
            />
          </VideoWrapper>
        )}
      </ImageWrapper>
    </ImageBrowser>
  );
}

ProductDetailImagesGrid.propTypes = {
  product: PropTypes.object,
};
